import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

import { Loader } from "@googlemaps/js-api-loader";
import { Grid, TextField } from "@material-ui/core";
import InputLabel from "@material-ui/core/InputLabel";
import { Field, useFormikContext } from "formik";

import ErrorMessage from "@Components/ErrorMessage";

import useStyles from "assets/jss/material-dashboard-react/components/inputStyle";
import { useTranslation } from "react-i18next";

const MAP_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
let myMap;
let marker;
let infoWindow;
let autoComplete;
let placeName;

let globalPositionFromFormik = null;

const MapField = (props) => {
  const { label, required, errorMessage, mapName } = props;
  const [gmapWin, setGmapWin] = useState(false);
  const [map, setMap] = useState(null);
  const [zoom] = useState(15);
  const [positionFromFormik, setPositionFromFormik] = useState(null);
  const [markerPosition, setMarkerPosition] = useState(null);
  const mapBox = useRef();
  const searchInputRef = useRef(null);
  const classes = useStyles();
  const { t } = useTranslation();
  const formik = useFormikContext();

  const [updateCenter, setUpdateCenter] = useState(true);

  useEffect(() => {
    if (markerPosition && markerPosition.lat && markerPosition.lng) {
      const submitValue = `[${markerPosition.lat}, ${markerPosition.lng}]`;
      console.log("Submit Value: ", submitValue);
      formik.setFieldValue(mapName, submitValue);
    }
  }, [markerPosition]);

  useEffect(() => {
    if (formik.values["branch_name"]) {
      placeName = formik.values["branch_name"];
    }
  }, [formik.values["branch_name"]]);

  useEffect(() => {
    if (formik.values[mapName]) {
      const formikValue = JSON.parse(formik.values[mapName]);
      const formikLat = formikValue[0];
      const formikLng = formikValue[1];
      const latLng = { lat: formikLat, lng: formikLng };
      setPositionFromFormik(latLng);
      globalPositionFromFormik = latLng;
    }
  }, [formik.values[mapName]]);

  useEffect(() => {
    const loader = new Loader({
      apiKey: MAP_API_KEY,
      version: "weekly",
      libraries: ["places", "geometry"],
    });
    const abortController = new AbortController();

    (async () => {
      loader.load().then(() => {
        if (
          !abortController.signal.aborted &&
          window.google?.maps &&
          !gmapWin
        ) {
          setGmapWin(true);
        }
        if (gmapWin) {
          myMap = new window.google.maps.Map(mapBox.current, {
            zoom: zoom,
            draggable: true,
          });
          setMap(myMap);
          infoWindow = new window.google.maps.InfoWindow();
        }
      });
    })();

    return () => {
      abortController.abort();
    };
  }, [gmapWin]);

  useEffect(() => {
    if (positionFromFormik && marker && myMap) {
      marker.setVisible(false);
      marker.setPosition(positionFromFormik);
      if (updateCenter) {
        myMap.setCenter(positionFromFormik);
      }
      marker.setVisible(true);
    }
  }, [positionFromFormik]);

  useEffect(() => {
    if (map) {
      marker = new window.google.maps.Marker({
        position: map.getCenter(),
        map: map,
        draggable: true,
        title: "Title",
      });
      marker.addListener("dragstart", () => {
        myMap.setOptions({ draggable: false });
      });
      marker.addListener("dragend", (markerMouseDragEvent) => {
        marker.setPosition(markerMouseDragEvent.latLng.toJSON());
        setMarkerPosition(markerMouseDragEvent.latLng.toJSON());
        myMap.setOptions({ draggable: true });
        setUpdateCenter(false);
      });

      marker.addListener("click", () => {
        if (placeName) {
          myMap.setZoom(18);
          infoWindow.setPosition(marker.getPosition());
          infoWindow.setContent(placeName);
          infoWindow.open(map);
        }
      });

      map.addListener("click", (mapsMouseEvent) => {
        marker.setPosition(mapsMouseEvent.latLng.toJSON());
        setMarkerPosition(mapsMouseEvent.latLng.toJSON());
        setUpdateCenter(false);
      });

      const options = {
        fields: ["formatted_address", "geometry", "name"],
        strictBounds: false,
        types: ["establishment"],
      };

      autoComplete = new window.google.maps.places.Autocomplete(
        searchInputRef.current,
        options
      );
      autoComplete.addListener("place_changed", onPlaceChanged);
      autoComplete.bindTo("bounds", map);

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const pos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            if (marker && !globalPositionFromFormik) {
              marker.setVisible(false);
              marker.setPosition(pos);
              myMap.setCenter(pos);
              marker.setVisible(true);
            }
          },
          () => {
            handleLocationError(true, infoWindow, map.getCenter());
          }
        );
      } else {
        handleLocationError(false, infoWindow, map.getCenter());
      }

      return () => {
        window.google.maps.event.clearInstanceListeners(searchInputRef);
        globalPositionFromFormik = null;
      };
    }
  }, [map]);

  const onPlaceChanged = () => {
    marker.setVisible(false);
    const place = autoComplete.getPlace();

    if (!place.geometry) return;
    if (place.geometry.viewport) {
      myMap.fitBounds(place.geometry.viewport);
    } else {
      myMap.setCenter(place.geometry.location);
      myMap.setZoom(15);
    }
    marker.setPosition(place.geometry.location);
    setMarkerPosition(place.geometry.location.toJSON());
    marker.setVisible(true);

    searchInputRef.current.blur();
  };

  const handleLocationError = (browserHasGeolocation, infoWindow, pos) => {
    infoWindow.setPosition(pos);
    infoWindow.setContent(
      browserHasGeolocation
        ? "Error: The Geolocation service failed."
        : "Error: Your browser doesn't support geolocation"
    );
    infoWindow.open(map);
  };

  return (
    <Field name={mapName}>
      {(props) => {
        const { meta } = props;

        return (
          <>
            <Grid item container xs={12}>
              <InputLabel
                htmlFor={mapName}
                className={clsx(classes.label, {
                  [classes.error]: errorMessage || (meta.error && meta.touched),
                })}
              >
                {t(label)}
                {required ? <span style={{ color: "red" }}> *</span> : null}
              </InputLabel>
              <Grid container item xs={12}>
                <Grid item xs={6}>
                  <TextField
                    inputRef={searchInputRef}
                    variant="outlined"
                    size="small"
                    // className={classes.root}
                    style={{ marginBottom: 8 }}
                    fullWidth
                    placeholder={t("Enter a location")}
                  />
                </Grid>
                <Grid item xs={12} style={{ height: 400 }} ref={mapBox} />
              </Grid>
            </Grid>
            <ErrorMessage message={errorMessage ? errorMessage : meta.error} />
          </>
        );
      }}
    </Field>
  );
};
MapField.propTypes = {
  labelClasses: PropTypes.object,
  errorMessage: PropTypes.string,
  label: PropTypes.string,
  mapName: PropTypes.string,
  required: PropTypes.bool,
  meta: PropTypes.object,
  field: PropTypes.object,
  form: PropTypes.object,
};

export default MapField;
